import React, { FC, useEffect, useState } from 'react';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { OrderInputType } from 'models/order-input/shared';
import { LabOrderContext } from './lab-order-context';
import LabOrder from './lab-order.component';
import { LabOrderInput } from 'models/order-input/lab-order';
import {
    emptyConstruction,
    extractScannerCaseTeethNumbers,
} from 'components/order-flow/lab-order/constructions-helper';
import { useParams } from 'react-router-dom';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { Box, CircularProgress } from '@mui/material';
import { ScannerCase, ScannerFileProcessingStatus } from 'models/scanner-case';
import { scannerCaseTemplate } from 'apollo/templates/scanner-case/query.graphql';
import { formatPinNumber, isValidPinNumber, requiresSwedishPinValidation } from 'utils/personal-identity-number';
import { dentistsWithClinicForPrepopulateTemplate } from 'apollo/templates/user';
import ScannerCaseSidebar from 'components/order-flow/lab-order/scanner-case-sidebar.component';
import { aiRestorationsTemplate } from 'apollo/templates/order';
import { RestorationData } from 'models/order-form';
import { startFileImportBackgroundProcessTemplate } from 'apollo/templates/scanner-case';
import { getClinicTemplate } from 'apollo/templates/organization/query.graphql';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import { useAssignInternalDentistId } from 'hooks/useAssignInternalDentistId';
import { resolvePrepopulatedDeliveryClinic } from 'utils/lab-dentist-default-clinic';

const LabOrderFromScannerCase: FC = () => {
    const { showBoundary } = useDenthubErrorBoundary();
    const [order, setOrder] = useState<LabOrderInput>({} as LabOrderInput);
    const [isTioTandvardPricingEnabled, setIsTioTandvardPricingEnabled] = useState<boolean>(false);
    const [isLoading, setLoading] = useState<boolean>(true);
    const currentOrg = currentOrgVar();
    const { caseId } = useParams<{ caseId: string }>();
    const { assignIdToDentist } = useAssignInternalDentistId();

    const isProductionPlanningEnabled = customerSettings.getCustomerSetting(
        currentOrg.customerSettings,
        CONSTANTS.SETTINGS_NAMES.PRODUCTION_PLANNING,
    );

    const [getScannerCase] = useLazyQuery(scannerCaseTemplate, {
        fetchPolicy: 'no-cache',
        variables: {
            caseId,
        },
        onCompleted({ scannerCase }: { scannerCase: ScannerCase }) {
            const construction = emptyConstruction();
            const { teethNumbers, teethState } = extractScannerCaseTeethNumbers(scannerCase);
            construction.teethNumbers = teethNumbers;
            construction.teethState = teethState;

            const requiresSwedishPin = requiresSwedishPinValidation(currentOrg);
            let pinNumber = scannerCase.patient?.patientRefNo || '';
            const isValidPin = isValidPinNumber(pinNumber);
            if (requiresSwedishPin && isValidPin) {
                pinNumber = formatPinNumber(pinNumber);
            }

            let deliveryDate = undefined;
            if (scannerCase.deliveryDate) {
                // Ensure deliveryDate is a Date object
                deliveryDate =
                    scannerCase.deliveryDate instanceof Date
                        ? scannerCase.deliveryDate
                        : new Date(scannerCase.deliveryDate);
            }

            setOrder({
                inputType: OrderInputType.NEW,
                supplier: { _id: currentOrg._id },
                isActionBasedOrder: false,
                customer: undefined,
                dentist: undefined,
                patient: {
                    personalIdentityNumber: pinNumber,
                    firstName: scannerCase.patient.firstName || '',
                    lastName: scannerCase.patient.lastName || '',
                    noPersonalIdentityNumber: requiresSwedishPin ? !isValidPin : !pinNumber,
                },
                constructions: [construction],
                comment: '',
                files: [],
                historicalComments: [],
                sample: { type: 'DIGITAL', content: [], otherContent: '' },
                deliveryDetails: {
                    date: deliveryDate,
                    destination: undefined,
                    pickUpLocation: undefined,
                    time: {
                        hour: deliveryDate ? deliveryDate.getHours() : undefined,
                        minute: deliveryDate ? deliveryDate.getMinutes() : undefined,
                    },
                },
                sampleReceivedDate: new Date(),
                internalDeliveryDate: null,
                externalDeliveryDate: null,
                productionPlanningSteps: [],
                scannerCase: scannerCase,
            } as LabOrderInput);

            if (shouldUseAiRestorations(scannerCase)) {
                getAiRestorations({ variables: { promt: scannerCase.comments } });
            }

            if (
                scannerCase.fileProcessingStatus === ScannerFileProcessingStatus.NOT_STARTED ||
                scannerCase.fileProcessingStatus === ScannerFileProcessingStatus.ERROR
            ) {
                startFileImportBackgroundProcess({ variables: { caseId: scannerCase._id } });
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [getDentists] = useLazyQuery(dentistsWithClinicForPrepopulateTemplate, {
        fetchPolicy: 'no-cache',
        onCompleted: async ({ dentistsWithClinicForPrepopulate: searchedDentists }) => {
            if (searchedDentists && searchedDentists.length > 0) {
                const [matchedDentist] = searchedDentists;
                // The dentist is attached to the order here rather than picked by hand, so
                // number them at this point too.
                const dentistLocalIds = await assignIdToDentist(matchedDentist._id, matchedDentist.clinic?._id);
                const defaultDeliveryClinic = resolvePrepopulatedDeliveryClinic(matchedDentist);
                setOrder((prevOrder) => ({
                    ...prevOrder,
                    dentist: dentistLocalIds ? { ...matchedDentist, dentistLocalIds } : matchedDentist,
                    deliveryDetails: {
                        ...prevOrder.deliveryDetails,
                        destination: defaultDeliveryClinic,
                        pickUpLocation: defaultDeliveryClinic,
                    },
                }));
                await getClinic({ variables: { id: matchedDentist.clinic._id } });
            }
            setLoading(false);
        },
        onError: (err) => {
            showBoundary(err);
        },
    });

    const [getClinic] = useLazyQuery(getClinicTemplate, {
        onError: (err) => {
            showBoundary(err);
        },
        onCompleted: async ({ clinic }) => {
            const isTioTandvardPricingEnabled = customerSettings.getCustomerSetting(
                clinic.parentClinicId ? clinic.parentClinic.customerSettings : clinic.customerSettings,
                CONSTANTS.SETTINGS_NAMES.ENABLE_TIOTANDVARD_PRICING,
            );
            const isTioTandvardPricingForLabEnabled = customerSettings.getCustomerSetting(
                currentOrg.customerSettings,
                CONSTANTS.SETTINGS_NAMES.ENABLE_TIOTANDVARD_PRICING_FOR_LAB,
            );
            // orderVersion is the backend's resolved answer - parent-clinic inheritance and the
            // local-clinic rule are already applied - so read it instead of re-deriving it from
            // the customer settings here.
            const isActionBasedClinic = clinic.orderVersion === CONSTANTS.SETTINGS_VALUES.ACTION_BASED;
            setIsTioTandvardPricingEnabled(
                isActionBasedClinic ? isTioTandvardPricingEnabled : isTioTandvardPricingForLabEnabled,
            );
            setOrder((prev) => {
                const updatedOrder = {
                    ...prev,
                    // Single writer of order.customer. clinic._id and isActionBasedOrder have to
                    // land in the same update: _id is what triggers the construction step to
                    // fetch a price list, and isActionBasedOrder is what picks which one.
                    customer: clinic,
                    isActionBasedOrder: isActionBasedClinic,
                };
                if (clinic.local || clinic.orderVersion === 'Simple') {
                    updatedOrder.deliveryDetails = {
                        ...prev.deliveryDetails,
                        pickUpLocation: clinic,
                        destination: clinic,
                    };
                }
                return updatedOrder;
            });
        },
    });

    const [getAiRestorations] = useLazyQuery(aiRestorationsTemplate, {
        onCompleted({ aiRestorations }: { aiRestorations: RestorationData[] }) {
            if (aiRestorations.length > 0) {
                const teeth = new Map<number, number>();
                aiRestorations.forEach((restoration) => {
                    if (restoration.teeth && restoration.teeth.length > 0) {
                        restoration.teeth.forEach((t: string) => {
                            const toothNumber = parseInt(t);
                            if (!isNaN(toothNumber)) {
                                teeth.set(toothNumber, 1);
                            }
                        });
                    }
                });
                if (teeth.size > 0) {
                    setOrder((prevOrder) => ({
                        ...prevOrder,
                        scannerCaseAiAssisted: true,
                        constructions: prevOrder.constructions.map((construction, index) => {
                            if (index === 0) {
                                return {
                                    ...construction,
                                    teethNumbers: teeth.size > 0 ? Array.from(teeth.keys()) : construction.teethNumbers,
                                    teethState: teeth.size > 0 ? teeth : construction.teethState,
                                };
                            }
                            return construction;
                        }),
                    }));
                }
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [startFileImportBackgroundProcess] = useMutation(startFileImportBackgroundProcessTemplate, {
        onError: (error) => {
            showBoundary(error);
        },
    });

    useEffect(() => {
        if (caseId) {
            getScannerCase({ variables: { caseId } });
        }
    }, [caseId]);

    useEffect(() => {
        if (order.scannerCase) {
            if (order.scannerCase.dentist?.name && order.scannerCase.clinic?.name) {
                getDentists({
                    variables: {
                        dentistName: order.scannerCase.dentist.name,
                        clinicName: order.scannerCase.clinic.name,
                    },
                });
            }
        }
        setLoading(false);
    }, [order.scannerCase]);

    const shouldUseAiRestorations = (scannerCase: ScannerCase) => {
        if (!scannerCase.comments) {
            return false;
        } else if (scannerCase.restorations === undefined || scannerCase.restorations.length === 0) {
            return true;
        } else if (scannerCase.restorations.length === 1) {
            const restoration = scannerCase.restorations[0];
            if ((restoration.teethNumbers === undefined || restoration.teethNumbers.length === 0) && !restoration.jaw) {
                return true;
            }
        }
        return false;
    };

    return (
        <LabOrderContext.Provider
            value={{
                order,
                setOrder,
                isTioTandvardPricingEnabled,
                setIsTioTandvardPricingEnabled,
                isProductionPlanningEnabled,
            }}
        >
            {isLoading || !order.supplier ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                    <Box>
                        <CircularProgress size={80} />
                    </Box>
                </Box>
            ) : (
                <Box display="flex">
                    <Box flexGrow={1}>
                        <LabOrder />
                    </Box>
                    <Box
                        sx={{
                            flexShrink: 0,
                            marginLeft: 2,
                            position: 'sticky',
                            top: '70px',
                            height: '100%',
                            overflow: 'hidden',
                        }}
                    >
                        {order.scannerCase ? <ScannerCaseSidebar scannerCase={order.scannerCase} /> : undefined}
                    </Box>
                </Box>
            )}
        </LabOrderContext.Provider>
    );
};

export default LabOrderFromScannerCase;
